import type {
  AmountFact,
  AmountKind,
  ContactFact,
  DateFact,
  DateKind,
  DocStatus,
  Fact,
  RefFact,
  RefKind,
  RequiredDocId
} from '../../types/document'
import { truncate, type Segment } from './text'

// Every extractor below returns only values literally present in the text,
// each paired with the sentence it came from. Nothing is inferred or defaulted.

function fact<T>(value: T, seg: Segment): Fact<T> {
  return { value, evidence: truncate(seg.text), certainty: seg.certainty }
}

// ── Dates ─────────────────────────────────────────────────────────────────────

const MONTHS: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4, may: 5, jun: 6, june: 6,
  jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12,
  // Hindi (with common spelling variants)
  'जनवरी': 1, 'फरवरी': 2, 'फ़रवरी': 2, 'मार्च': 3, 'अप्रैल': 4, 'अप्रेल': 4, 'मई': 5, 'जून': 6, 'जुलाई': 7,
  'अगस्त': 8, 'सितंबर': 9, 'सितम्बर': 9, 'अक्टूबर': 10, 'अक्तूबर': 10, 'नवंबर': 11, 'नवम्बर': 11,
  'दिसंबर': 12, 'दिसम्बर': 12,
  // Marathi
  'जानेवारी': 1, 'फेब्रुवारी': 2, 'एप्रिल': 4, 'मे': 5, 'जुलै': 7, 'ऑगस्ट': 8, 'सप्टेंबर': 9,
  'ऑक्टोबर': 10, 'नोव्हेंबर': 11, 'डिसेंबर': 12,
  // Bengali / Assamese
  'জানুয়ারি': 1, 'ফেব্রুয়ারি': 2, 'মার্চ': 3, 'এপ্রিল': 4, 'মে': 5, 'জুন': 6, 'জুলাই': 7, 'আগস্ট': 8,
  'সেপ্টেম্বর': 9, 'অক্টোবর': 10, 'নভেম্বর': 11, 'ডিসেম্বর': 12,
  'জানুয়ারী': 1, 'ফেব্রুয়ারী': 2, 'আগষ্ট': 8,
  // Assamese
  'জানুৱাৰী': 1, 'জানুৱাৰি': 1, 'ফেব্ৰুৱাৰী': 2, 'ফেব্ৰুৱাৰি': 2, 'মাৰ্চ': 3, 'এপ্ৰিল': 4, 'ছেপ্টেম্বৰ': 9,
  'চেপ্টেম্বৰ': 9, 'অক্টোবৰ': 10, 'নৱেম্বৰ': 11, 'ডিচেম্বৰ': 12, 'ডিচেম্বাৰ': 12,
  // Tamil
  'ஜனவரி': 1, 'பிப்ரவரி': 2, 'மார்ச்': 3, 'ஏப்ரல்': 4, 'மே': 5, 'ஜூன்': 6, 'ஜூலை': 7, 'ஆகஸ்ட்': 8,
  'ஆகஸ்டு': 8, 'செப்டம்பர்': 9, 'அக்டோபர்': 10, 'நவம்பர்': 11, 'டிசம்பர்': 12,
  // Telugu
  'జనవరి': 1, 'ఫిబ్రవరి': 2, 'మార్చి': 3, 'మార్చ్': 3, 'ఏప్రిల్': 4, 'మే': 5, 'జూన్': 6, 'జూలై': 7,
  'ఆగస్టు': 8, 'ఆగస్ట్': 8, 'సెప్టెంబర్': 9, 'సెప్టెంబరు': 9, 'అక్టోబర్': 10, 'అక్టోబరు': 10,
  'నవంబర్': 11, 'నవంబరు': 11, 'డిసెంబర్': 12, 'డిసెంబరు': 12,
  // Gujarati
  'જાન્યુઆરી': 1, 'ફેબ્રુઆરી': 2, 'માર્ચ': 3, 'એપ્રિલ': 4, 'મે': 5, 'જૂન': 6, 'જુલાઈ': 7, 'ઑગસ્ટ': 8,
  'ઓગસ્ટ': 8, 'સપ્ટેમ્બર': 9, 'ઑક્ટોબર': 10, 'ઓક્ટોબર': 10, 'નવેમ્બર': 11, 'ડિસેમ્બર': 12,
  // Kannada
  'ಜನವರಿ': 1, 'ಫೆಬ್ರವರಿ': 2, 'ಮಾರ್ಚ್': 3, 'ಏಪ್ರಿಲ್': 4, 'ಮೇ': 5, 'ಜೂನ್': 6, 'ಜುಲೈ': 7, 'ಆಗಸ್ಟ್': 8,
  'ಸೆಪ್ಟೆಂಬರ್': 9, 'ಅಕ್ಟೋಬರ್': 10, 'ನವೆಂಬರ್': 11, 'ಡಿಸೆಂಬರ್': 12,
  // Malayalam
  'ജനുവരി': 1, 'ഫെബ്രുവരി': 2, 'മാർച്ച്': 3, 'ഏപ്രിൽ': 4, 'മേയ്': 5, 'മെയ്': 5, 'ജൂൺ': 6, 'ജൂലൈ': 7,
  'ഓഗസ്റ്റ്': 8, 'ആഗസ്റ്റ്': 8, 'സെപ്റ്റംബർ': 9, 'ഒക്ടോബർ': 10, 'നവംബർ': 11, 'ഡിസംബർ': 12,
  // Punjabi
  'ਜਨਵਰੀ': 1, 'ਫ਼ਰਵਰੀ': 2, 'ਫਰਵਰੀ': 2, 'ਮਾਰਚ': 3, 'ਅਪ੍ਰੈਲ': 4, 'ਅਪਰੈਲ': 4, 'ਮਈ': 5, 'ਜੂਨ': 6,
  'ਜੁਲਾਈ': 7, 'ਅਗਸਤ': 8, 'ਸਤੰਬਰ': 9, 'ਅਕਤੂਬਰ': 10, 'ਨਵੰਬਰ': 11, 'ਦਸੰਬਰ': 12,
  // Odia
  'ଜାନୁଆରୀ': 1, 'ଜାନୁଆରି': 1, 'ଫେବୃଆରୀ': 2, 'ଫେବ୍ରୁଆରୀ': 2, 'ମାର୍ଚ୍ଚ': 3, 'ମାର୍ଚ': 3, 'ଏପ୍ରିଲ': 4,
  'ମଇ': 5, 'ମେ': 5, 'ଜୁନ': 6, 'ଜୁଲାଇ': 7, 'ଅଗଷ୍ଟ': 8, 'ସେପ୍ଟେମ୍ବର': 9, 'ଅକ୍ଟୋବର': 10, 'ନଭେମ୍ବର': 11,
  'ଡିସେମ୍ବର': 12
}

// Text is NFC-normalised in cleanLine(), so keys are too: nukta letters
// (फ़, ਫ਼) match however they were typed or recognised.
for (const key of Object.keys(MONTHS)) MONTHS[key.normalize('NFC')] = MONTHS[key]

const MONTH_ALT = Object.keys(MONTHS).sort((a, b) => b.length - a.length).join('|')
const NB = '(?<![\\p{L}\\p{M}\\p{N}])' // not preceded by a letter/digit
const NA = '(?![\\p{L}\\p{M}\\p{N}])' // not followed by a letter/digit

const DATE_PATTERNS: Array<{ re: RegExp; parse: (m: RegExpMatchArray) => [number, number, number] }> = [
  // 12/09/2026, 12-09-2026, 12.09.26
  {
    re: new RegExp(`${NB}(\\d{1,2})\\s?[./-]\\s?(\\d{1,2})\\s?[./-]\\s?(\\d{4}|\\d{2})${NA}`, 'gu'),
    parse: (m) => [Number(m[1]), Number(m[2]), Number(m[3])]
  },
  // 2026-09-12
  {
    re: new RegExp(`${NB}(\\d{4})-(\\d{1,2})-(\\d{1,2})${NA}`, 'gu'),
    parse: (m) => [Number(m[3]), Number(m[2]), Number(m[1])]
  },
  // 12 September 2026, 12th Sept, 2026, 12 सितंबर 2026
  {
    re: new RegExp(`${NB}(\\d{1,2})(?:st|nd|rd|th)?\\s*(?:of\\s+)?(${MONTH_ALT})[.,]?\\s*,?\\s*(\\d{4})(?!\\p{N})`, 'giu'),
    parse: (m) => [Number(m[1]), MONTHS[m[2].toLowerCase().normalize('NFC')], Number(m[3])]
  },
  // September 12, 2026
  {
    re: new RegExp(`${NB}(${MONTH_ALT})[.,]?\\s+(\\d{1,2})(?:st|nd|rd|th)?,?\\s+(\\d{4})(?!\\p{N})`, 'giu'),
    parse: (m) => [Number(m[2]), MONTHS[m[1].toLowerCase().normalize('NFC')], Number(m[3])]
  }
]

const DATE_KIND_RULES: Array<{ kind: DateKind; before?: RegExp; after?: RegExp }> = [
  { kind: 'birth', before: /(date of birth|d\.?o\.?b|birth|जन्म|জন্ম)/i },
  { kind: 'validity', before: /(valid\s*(till|up\s*to|upto|until)|validity|expir|वैध|मान्य|वैधता)/i },
  { kind: 'hearing', before: /(hearing|appear|appearance|next date|सुनवाई|पेशी|उपस्थित|हाजिर|হাজির|শুনানি)/i },
  {
    kind: 'deadline',
    before: /(last date|deadline|on or before|not later than|latest by|before|within|\bby\s*$|अंतिम (तिथि|तारीख|दिनांक)|अन्तिम (तिथि|तारीख)|आखिरी तारीख|आख़िरी तारीख़|शेवटची तारीख|শেষ তারিখ|শেষ তাৰিখ|கடைசி தேதி|கடைசி நாள்|చివరి తేదీ|ಕೊನೆಯ ದಿನಾಂಕ|അവസാന തീയതി|છેલ્લી તારીખ|ਆਖਰੀ ਮਿਤੀ|ਆਖ਼ਰੀ ਤਾਰੀਖ|ଶେଷ ତାରିଖ)/i,
    after: /^\s*(तक|से पहले|से पूर्व|पर्यंत|पूर्वी|পর্যন্ত|এর মধ্যে|க்குள்|లోపు|સુધી|ಒಳಗೆ|ਤੱਕ|ମଧ୍ୟରେ)/
  },
  { kind: 'due', before: /(due date|due on|pay by|payable by|देय (तिथि|तारीख|दिनांक)|भुगतान की तिथि)/i },
  { kind: 'issue', before: /(date of issue|issue date|issued on|bill date|dated|^\s*date|\bdt\.?|जारी|निर्गत|दिनांक|दि\.|तारीख|দিনাংক|তারিখ)/i }
]

// "Last date … (long clause) … 10.02.2026": strong phrases that make the first
// date after them in the same sentence a deadline, however far away it is.
const STRONG_DEADLINE = /(last date|deadline|closing date|अंतिम (तिथि|तारीख|दिनांक)|अन्तिम (तिथि|तारीख)|आखिरी तारीख|आख़िरी तारीख़|शेवटची तारीख|শেষ তারিখ|கடைசி தேதி|చివరి తేదీ|ಕೊನೆಯ ದಿನಾಂಕ|അവസാന തീയതി|છેલ્લી તારીખ|ਆਖਰੀ ਮਿਤੀ|ଶେଷ ତାରିଖ)/gi
const EXTENDED_TILL = /(extended (till|to|up\s*to|upto)|\btill\s*$|\buntil\s*$|\bupto\s*$|\bup to\s*$|तक बढ़ा)/i


function toIso(day: number, month: number, year: number): string | null {
  const fullYear = year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year
  if (month < 1 || month > 12 || day < 1 || day > 31 || fullYear < 1900 || fullYear > 2100) return null
  const date = new Date(Date.UTC(fullYear, month - 1, day))
  if (date.getUTCMonth() !== month - 1) return null
  return `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function extractDates(segments: Segment[]): DateFact[] {
  const found: DateFact[] = []
  for (const seg of segments) {
    const taken: Array<[number, number]> = []
    for (const { re, parse } of DATE_PATTERNS) {
      for (const m of seg.text.matchAll(re)) {
        const start = m.index ?? 0
        const end = start + m[0].length
        if (taken.some(([s, e]) => start < e && end > s)) continue
        const iso = toIso(...parse(m))
        if (!iso) continue
        taken.push([start, end])
        const before = seg.text.slice(Math.max(0, start - 60), start)
        const after = seg.text.slice(end, end + 20)
        const rule = DATE_KIND_RULES.find((r) => (r.before && r.before.test(before)) || (r.after && r.after.test(after)))
        let kind: DateKind = rule?.kind ?? 'other'
        // An "issue" date must sit in a short header line ("Date: …", "पत्रांक … दिनांक …");
        // in a long sentence "dated …" usually refers to some other order or notice.
        if (kind === 'issue' && seg.text.length > 90) kind = 'other'
        if (kind === 'other' && EXTENDED_TILL.test(before)) kind = 'deadline'
        if (kind === 'other') {
          const keywordEnd = [...seg.text.slice(0, start).matchAll(STRONG_DEADLINE)].map((k) => (k.index ?? 0) + k[0].length).pop()
          const between = keywordEnd === undefined ? '' : seg.text.slice(keywordEnd, start)
          if (keywordEnd !== undefined && !DATE_PATTERNS.some(({ re }) => new RegExp(re.source, re.flags).test(between))) kind = 'deadline'
        }
        found.push({ ...fact(m[0].trim(), seg), kind, iso })
      }
    }
  }
  const seen = new Set<string>()
  const specific = new Set(found.filter((d) => d.kind !== 'other').map((d) => d.iso))
  return found.filter((d) => {
    if (d.kind === 'other' && specific.has(d.iso)) return false
    const key = `${d.iso}|${d.kind}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** The date the reader must act by, in priority order. Never guessed from 'other' dates. */
export function pickDeadline(dates: DateFact[]): DateFact | null {
  for (const kind of ['deadline', 'due', 'hearing'] as DateKind[]) {
    const match = dates.find((d) => d.kind === kind)
    if (match) return match
  }
  return null
}

// ── Amounts ───────────────────────────────────────────────────────────────────

const CURRENCY_BEFORE = /(?:₹|\brs\.?|\binr\b|रु\.?|रुपये|रुपए|रूपये|টাকা)\s*:?\s*(\d[\d,]*(?:\.\d{1,2})?)/giu
const CURRENCY_AFTER = /(?<![\d.])(\d[\d,]*(?:\.\d{1,2})?)\s*(?:\/-|रुपये|रुपए|रूपये|rupees|টাকা)/giu
const LABELLED_AMOUNT = /(amount payable|net payable|total payable|total due|amount due|bill amount|payable amount|annual income|total income|late fee|penalty|fine|fees?|देय राशि|कुल देय|कुल राशि|वार्षिक आय|कुल आय|जुर्माना|शुल्क|एकूण उत्पन्न|वार्षिक उत्पन्न)\s*(?:\([^)]*\))?\s*[:=-]\s*(\d[\d,]*(?:\.\d{1,2})?)/giu

const AMOUNT_KIND_RULES: Array<{ kind: AmountKind; re: RegExp }> = [
  { kind: 'fine', re: /(penalty|fine|late (payment )?(fee|surcharge)|जुर्माना|अर्थदंड|दंड|विलंब शुल्क|दंड|জরিমানা)/i },
  { kind: 'income', re: /(income|आय|आमदनी|उत्पन्न|আয়)/i },
  { kind: 'payable', re: /(payable|total due|amount due|dues|bill amount|pay |outstanding|देय|बकाया|भुगतान|भरावे|জমা দিন|বকেয়া)/i },
  { kind: 'sanctioned', re: /(sanction|credited|disburs|instal+ment|transferred|benefit|स्वीकृत|किस्त|क़िस्त|अनुदान|सहायता राशि|मंजूर)/i },
  { kind: 'fee', re: /(fees?\b|charges|शुल्क|फीस|फ़ीस)/i }
]

export function extractAmounts(segments: Segment[]): AmountFact[] {
  const found: AmountFact[] = []
  for (const seg of segments) {
    const taken: Array<[number, number]> = []
    for (const re of [LABELLED_AMOUNT, CURRENCY_BEFORE, CURRENCY_AFTER]) {
      for (const m of seg.text.matchAll(re)) {
        const numeric = m[re === LABELLED_AMOUNT ? 2 : 1]
        const start = m.index ?? 0
        const end = start + m[0].length
        if (taken.some(([s, e]) => start < e && end > s)) continue
        const rupees = Number(numeric.replace(/,/g, ''))
        // 10-digit numbers are phone numbers, not money
        if (!Number.isFinite(rupees) || rupees <= 0 || rupees >= 1e10 || /^\d{10}$/.test(numeric)) continue
        taken.push([start, end])
        const context = seg.text.slice(Math.max(0, start - 50), end + 10)
        const kind = AMOUNT_KIND_RULES.find((r) => r.re.test(context))?.kind ?? 'other'
        found.push({ ...fact(formatRupees(rupees), seg), kind, rupees })
      }
    }
  }
  const seen = new Set<string>()
  return found.filter((a) => {
    const key = `${a.rupees}|${a.kind}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function formatRupees(rupees: number): string {
  return `₹${rupees.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
}

// ── Reference numbers ─────────────────────────────────────────────────────────

const REF_VALUE = '([A-Za-z0-9][A-Za-z0-9/_.\\-]*[A-Za-z0-9](?:\\s\\d{3,})*)'
const REF_LABELS: Array<{ kind: RefKind; re: RegExp }> = [
  { kind: 'application', re: new RegExp(`(?:application|appl\\.?|acknowledge?ment|ack\\.?|आवेदन|अर्ज)\\s*(?:no\\.?|number|id|संख्या|सं\\.?|क्रमांक|क्र\\.?|नं\\.?)\\s*[:.\\-–]?\\s*${REF_VALUE}`, 'giu') },
  { kind: 'certificate', re: new RegExp(`(?:certificate|cert\\.?|प्रमाण\\s*-?पत्र|प्रमाणपत्र|दाखला)\\s*(?:no\\.?|number|संख्या|सं\\.?|क्रमांक|क्र\\.?|नं\\.?)\\s*[:.\\-–]?\\s*${REF_VALUE}`, 'giu') },
  { kind: 'consumer', re: new RegExp(`(?:consumer|customer|service|connection|k|ca|उपभोक्ता|ग्राहक)\\s*(?:no\\.?|number|id|संख्या|सं\\.?|क्रमांक|नं\\.?)\\s*[:.\\-–]?\\s*${REF_VALUE}`, 'giu') },
  { kind: 'account', re: new RegExp(`(?:account|a/c|loan|policy|खाता|ऋण|पॉलिसी)\\s*(?:no\\.?|number|संख्या|सं\\.?|क्रमांक|नं\\.?)\\s*[:.\\-–]?\\s*${REF_VALUE}`, 'giu') },
  { kind: 'case', re: new RegExp(`(?:case|suit|complaint|crime|fir|प्रकरण|वाद|मुकदमा|केस|शिकायत)\\s*(?:no\\.?|number|संख्या|सं\\.?|क्रमांक|नं\\.?)\\s*[:.\\-–]?\\s*${REF_VALUE}`, 'giu') },
  { kind: 'registration', re: new RegExp(`(?:registration|regn\\.?|reg\\.?|enrol(?:l)?ment|ration card|पंजीकरण|नामांकन|राशन\\s*कार्ड)\\s*(?:no\\.?|number|id|संख्या|सं\\.?|क्रमांक|नं\\.?)\\s*[:.\\-–]?\\s*${REF_VALUE}`, 'giu') },
  { kind: 'receipt', re: new RegExp(`(?:receipt|challan|bill|token|रसीद|चालान|बिल)\\s*(?:no\\.?|number|संख्या|सं\\.?|क्रमांक|नं\\.?)\\s*[:.\\-–]?\\s*${REF_VALUE}`, 'giu') },
  { kind: 'reference', re: new RegExp(`(?:^|\\s)(?:ref(?:erence)?\\.?|letter|memo|order|file|notice|पत्रांक|पत्र\\s*सं\\.?|आदेश\\s*सं\\.?|जावक\\s*क्र\\.?)\\s*(?:no\\.?|number)?\\s*[:.\\-–]\\s*${REF_VALUE}`, 'giu') }
]

export function extractReferences(segments: Segment[]): RefFact[] {
  const found: RefFact[] = []
  const seen = new Set<string>()
  for (const seg of segments) {
    for (const { kind, re } of REF_LABELS) {
      for (const m of seg.text.matchAll(re)) {
        const value = m[1].replace(/[.\-/]+$/, '')
        if (!/\d/.test(value) || value.length < 3 || seen.has(value)) continue
        seen.add(value)
        found.push({ ...fact(value, seg), kind })
      }
    }
  }
  return found.slice(0, 6)
}

// ── Person the document is about ──────────────────────────────────────────────

const NAME_STOP = /\s*(?:\b(?:s\/o|d\/o|w\/o|r\/o|c\/o|son|daughter|wife|husband|resident|aged?|father|mother|address|village|dob|date|mobile)\b|पुत्र|पुत्री|पत्नी|पति|निवासी|आयु|उम्र|पिता|माता|पता|ग्राम|जन्म|[,;:(\d]).*$/iu
const NAME_LABEL = /(?:^|\s)(?:(?:applicant|beneficiary|consumer|candidate|student|account holder|holder|customer|patient)(?:'s)?\s+name|name(?:\s+of\s+(?:the\s+)?(?:applicant|beneficiary|candidate|consumer|holder|student|account holder))?|आवेदक का नाम|आवेदिका का नाम|लाभार्थी का नाम|उपभोक्ता का नाम|प्रार्थी का नाम|नाम|नांव|নাম|பெயர்|పేరు|નામ|ಹೆಸರು|പേര്|ਨਾਮ|ନାମ)\s*[:：\-–]\s*(.{2,70})/iu
const CERTIFY_NAME = /(?:certif(?:y|ied) that|प्रमाणित किया जाता है कि|प्रमाणित किया जाता है की|प्रमाणित करण्यात येते की)\s*(?:shri|smt|kumari|km|mr|mrs|ms|sri|srimati|श्री|श्रीमती|कुमारी|सुश्री|कु\.)?\.?\s*(.{2,70})/iu
const TO_NAME = /^[^\p{L}]*(?:to,?\s+)?(?:shri|smt|mr|mrs|ms|sri|श्री|श्रीमती|सुश्री)\.?\s+(.{2,60})/iu
const NOT_A_NAME = /(office|department|government|officer|manager|branch|district|court|bank|कार्यालय|विभाग|सरकार|अधिकारी|प्रबंधक|जिला|न्यायालय|बैंक|father|पिता)/i

function cleanName(raw: string): string | null {
  const name = raw.replace(NAME_STOP, '').replace(/[^\p{L}\p{M}.\s'-]/gu, '').replace(/\s+/g, ' ').trim()
  const words = name.split(' ').filter(Boolean)
  if (words.length === 0 || words.length > 5 || name.length < 3 || NOT_A_NAME.test(name)) return null
  return name
}

export function extractPerson(segments: Segment[]): Fact | null {
  for (const re of [NAME_LABEL, CERTIFY_NAME]) {
    for (const seg of segments) {
      const m = seg.text.match(re)
      const name = m ? cleanName(m[1]) : null
      if (name) return fact(name, seg)
    }
  }
  // "To, Shri Ram Kumar" addressee block near the top
  for (const seg of segments.slice(0, 12)) {
    const m = seg.text.match(TO_NAME)
    const name = m ? cleanName(m[1]) : null
    if (name) return fact(name, seg)
  }
  return null
}

// ── Issuing office / authority ────────────────────────────────────────────────

const AUTHORITY_STRONG = /(office of|department|directorate|municipal|nagar (nigam|palika|panchayat)|gram panchayat|tehsil|tahsil|collector|district magistrate|sub[- ]divisional|block development|court|tribunal|bank|corporation|nigam|board|authority|police|commission|ministry|uidai|income tax|कार्यालय|विभाग|निदेशालय|नगर (निगम|पालिका|पंचायत)|ग्राम पंचायत|तहसील|जिलाधिकारी|कलेक्टर|उपजिलाधिकारी|न्यायालय|बैंक|निगम|बोर्ड|प्राधिकरण|पुलिस|आयोग|मंत्रालय|आयकर|कार्यालय|विद्युत|কার্যালয়|দপ্তর|অফিস)/i
const AUTHORITY_WEAK = /(government of|govt\.? of|सरकार|शासन|state of|भारत सरकार)/i

export function extractAuthority(segments: Segment[]): Fact | null {
  const scored = segments
    .map((seg, index) => {
      // Letterheads and signature blocks are short and are not instructions
      // Letterheads at the very top can be long ("Government of India, Ministry of …, Staff Selection Commission")
      const maxLength = index < 6 ? 150 : 90
      if (seg.text.length > maxLength || seg.text.length < 4 || (ACTION_VERB.test(seg.text) && DIRECTIVE.test(seg.text))) return null
      // Web addresses and bracketed notes ("(To be uploaded on the website…)") are not letterheads
      if (/https?:|www\.|^\(/i.test(seg.text)) return null
      const nearEdge = index < 10 || index >= segments.length - 6
      if (!nearEdge) return null
      const score = (AUTHORITY_STRONG.test(seg.text) ? 2 : 0) + (AUTHORITY_WEAK.test(seg.text) ? 1 : 0) + (index < 6 ? 1 : 0)
      return score >= 2 ? { seg, score, index } : null
    })
    .filter((x): x is { seg: Segment; score: number; index: number } => x !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
  if (scored.length === 0) return null
  const best = scored[0]
  return fact(best.seg.text.replace(/[,:\s]+$/, ''), best.seg)
}

// ── Where to go ───────────────────────────────────────────────────────────────

const VENUE_LABEL = /^(?:venue|place|place of hearing|office address|camp|centre|center|स्थान|स्थल|पता|कार्यालय का पता|ठिकाण|স্থান)\s*[:：\-–]\s*(.{3,120})/iu
const GO_VERB = /(visit|appear|report|come to|present (yourself|in person)|contact|submit|attend|उपस्थित|आएं|आयें|पहुंचें|पहुँचें|संपर्क|जमा|हजर|হাজির|যোগাযোগ)/i
const GO_PLACE = /(office|branch|court|centre|center|camp|tehsil|block|panchayat|counter|kendra|csc|portal|website|कार्यालय|शाखा|न्यायालय|केंद्र|केन्द्र|तहसील|ब्लॉक|शिविर|पंचायत|काउंटर|पोर्टल|वेबसाइट|কার্যালয়|অফিস|শাখা)/i

// A short "at the school office" / "तहसील कार्यालय में" phrase inside a sentence
const PLACE_PHRASE_EN = /\b(?:at|to|in|visit)\s+(?:the\s+)?((?:[A-Za-z.,'-]+\s+){0,4}?(?:office|branch|court|centre|center|camp|tehsil|block office|panchayat|counter|kendra|csc|bank))\b/i
const PLACE_PHRASE_HI = /((?:[\p{L}\p{M}]+\s+){0,3}?(?:कार्यालय|दफ्तर|दफ़्तर|शाखा|न्यायालय|केंद्र|केन्द्र|तहसील|ब्लॉक|शिविर|पंचायत|काउंटर))\s*(?:में|पर|मे)/u

const PLACE_STOPWORDS = /^(तक|के|में|से|को|पर|एवं|और|साथ|की|का|हेतु|लिए|पुनः|the|a|an|and|with|your|or)$/i

export function extractPlace(segments: Segment[]): Fact | null {
  for (const seg of segments) {
    const m = seg.text.match(VENUE_LABEL)
    if (m) return fact(m[1].trim(), seg)
  }
  const sentence = segments.find((seg) => GO_VERB.test(seg.text) && GO_PLACE.test(seg.text) && seg.text.length < 300)
  if (!sentence) return null
  const phrase = (sentence.text.match(PLACE_PHRASE_EN)?.[1] ?? sentence.text.match(PLACE_PHRASE_HI)?.[1] ?? '')
    .split(' ')
    .filter((word, index, words) => !(index < words.length - 1 && PLACE_STOPWORDS.test(word)))
    .join(' ')
  // Without a clear phrase, the sentence itself is shown as the evidence only
  return fact(phrase?.trim() ?? '', sentence)
}

// ── Contacts ──────────────────────────────────────────────────────────────────

const CONTACT_CONTEXT = /(helpline|help line|toll[- ]?free|phone|ph\.?|tel\.?|mobile|mob\.?|contact|call|whatsapp|enquiry|inquiry|हेल्पलाइन|फोन|फ़ोन|मोबाइल|संपर्क|दूरभाष|टोल फ्री|ফোন|যোগাযোগ)/i
const PHONE = /(?<!\d)(1800[\s-]?\d{3}[\s-]?\d{3,4}|(?:\+91[\s-]?)?[6-9]\d{4}[\s-]?\d{5}|0\d{2,4}[\s-]?\d{6,8})(?!\d)/g
const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g
const WEBSITE = /\b(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:gov\.in|nic\.in|gov|in|org|com)(?:\/[^\s)]*)?/gi

export function extractContacts(segments: Segment[]): ContactFact[] {
  const found: ContactFact[] = []
  const seen = new Set<string>()
  const push = (value: string, kind: ContactFact['kind'], seg: Segment) => {
    const key = value.toLowerCase().replace(/[\s-]/g, '')
    if (seen.has(key)) return
    seen.add(key)
    found.push({ ...fact(value, seg), kind })
  }
  for (const seg of segments) {
    for (const m of seg.text.matchAll(EMAIL)) push(m[0], 'email', seg)
    const withoutEmails = seg.text.replace(EMAIL, ' ')
    for (const m of withoutEmails.matchAll(WEBSITE)) {
      // Skip short OCR fragments like "a.in"; keep "uppcl.org", "www.x.com", "pmkisan.gov.in"
      if (!/^(https?:|www\.)/i.test(m[0]) && m[0].replace(/\.(gov\.in|nic\.in|gov|in|org|com)$/i, '').length < 4) continue
      push(m[0].replace(/[.,]+$/, ''), 'website', seg)
    }
    // Only numbers labelled as contact numbers — never the applicant's own mobile
    if (CONTACT_CONTEXT.test(seg.text)) {
      for (const m of seg.text.matchAll(PHONE)) push(m[1].trim(), 'phone', seg)
    }
  }
  return found.slice(0, 4)
}

// ── Status ────────────────────────────────────────────────────────────────────

const CONDITIONAL = /(\bif\b|\bunless\b|otherwise|failing which|in case|\bmay\b|\bwill\b|\bshall\b|or reject|approval or|यदि|अगर|अन्यथा|नहीं तो|की स्थिति में|जाएगा|जायेगा|जाएगी|जायेगी|जावेगा|जावेगी|या अस्वीकृ|स्वीकृति या|जर)/i
// A status must be about the reader's own application, not a general rule
const ADDRESSES_READER = /(\byour\b|\byou\b|applicant|status\s*[:：-]|आपका|आपकी|आपके|आवेदक|आवेदिका|प्रार्थी|स्थिति\s*[:：-]|तुमचा|तुमची|আপনার|আপোনাৰ|உங்கள்|మీ |ನಿಮ್ಮ|നിങ്ങളുടെ|તમારી|તમારો|ਤੁਹਾਡ|ଆପଣଙ୍କ)/i
const STATUS_RULES: Array<{ status: DocStatus; re: RegExp }> = [
  { status: 'rejected', re: /(not (been )?(approved|accepted|sanctioned)|rejected|declined|disapproved|refused|अस्वीकृत|अस्वीकार|निरस्त|खारिज|नामंजूर|नामंज़ूर|अमान्य|फेटाळ|বাতিল|প্রত্যাখ্যান)/i },
  { status: 'cancelled', re: /(cancelled|canceled|revoked|withdrawn|terminated|रद्द|निरसित|समाप्त कर दिया)/i },
  { status: 'approved', re: /(approved|sanctioned|accepted|granted|verified successfully|स्वीकृत|स्वीकार कर|मंजूर|मंज़ूर|अनुमोदित|स्वीकारण्यात|মঞ্জুর|অনুমোদিত)/i },
  { status: 'pending', re: /(pending|under (process|review|verification|consideration)|in process|लंबित|प्रक्रियाधीन|विचाराधीन|प्रलंबित|প্রক্রিয়াধীন)/i }
]
const STATUS_SUBJECT = /(application|request|claim|status|loan|scholarship|pension|आवेदन|अनुरोध|दावा|स्थिति|ऋण|छात्रवृत्ति|पेंशन|अर्ज|আবেদন)/i

export function extractStatus(segments: Segment[]): Fact<DocStatus> | null {
  const hits: Array<Fact<DocStatus>> = []
  for (const seg of segments) {
    if (CONDITIONAL.test(seg.text) || !STATUS_SUBJECT.test(seg.text) || !ADDRESSES_READER.test(seg.text)) continue
    const rule = STATUS_RULES.find((r) => r.re.test(seg.text))
    if (rule) hits.push(fact(rule.status, seg))
  }
  if (hits.length === 0) return null
  // Contradictory statuses: report nothing rather than pick one
  const distinct = new Set(hits.map((h) => h.value))
  if (distinct.size > 1) return null
  return hits[0]
}

// ── Actions & warnings ────────────────────────────────────────────────────────

const ACTION_VERB = /\b(submit|apply|visit|appear|pay|deposit|bring|attach|enclose|contact|report|furnish|produce|complete|update|link|register|renew|collect|verify|file|respond|reply|upload|present|carry|obtain|get)\b|जमा कर|जमा करा|उपस्थित हो|उपस्थित रह|भुगतान कर|संपर्क कर|प्रस्तुत कर|लेकर आ|साथ ला|आवेदन कर|अद्यतन कर|अपडेट कर|भरें|पूर्ण कर|पूरा कर|करवा|कराएं|करायें|करें|कीजिए|सादर करा|भरावे|हजर रहा|संपर्क साधा|করুন|দিন|জমা দিয়ক|কৰক|செலுத்த|சமர்ப்பி|தொடர்பு கொள்|సమర్పించ|చెల్లించ|సంప్రదించ|જમા કરાવ|રજૂ કર|સંપર્ક કર|ભરો|ಸಲ್ಲಿಸ|ಪಾವತಿಸ|ಸಂಪರ್ಕಿಸ|സമർപ്പിക്ക|അടയ്ക്ക|ബന്ധപ്പെട|ਜਮ੍ਹਾ ਕਰ|ਸੰਪਰਕ ਕਰ|ਭਰੋ|ଜମା କର|ଯୋଗାଯୋଗ କର/i
const DIRECTIVE = /(you are (hereby )?(requested|required|directed|advised|informed|asked)|please|kindly|\bmust\b|\bshould\b|\bshall\b|is required|are required|is mandatory|mandatory|necessary|ensure|on or before|कृपया|अनिवार्य|आवश्यक|सुनिश्चित|निर्देशित|अनुरोध है|होगा|होगी|करें|कीजिए|करावे|करुन घ्यावे|করুন|দিন|কৰক|দিয়ক|வேண்டும்|வும்|ங்கள்|గలరు|ండి|કરવું|કરો|કરાવો|ಬೇಕು|ಿರಿ|ണം|ുക|ਕਰੋ|ਕਰਵਾਓ|କରନ୍ତୁ)/i
const IMPERATIVE_START = /^\s*(please\s+)?(submit|apply|visit|appear|pay|deposit|bring|attach|enclose|contact|report|furnish|produce|complete|update|link|register|renew|collect|upload|carry)\b/i
const NOT_ACTION = /(this is to certify|certified that|प्रमाणित किया जाता है|प्रमाणित करण्यात)/i

// "Last date for receipt of online applications: 07.10.2026" states a task
// (apply / pay / submit by that date) even without an imperative verb
const DEADLINE_TASK = /(application|apply|submission|submit|payment|fee|form|आवेदन|जमा|भुगतान|शुल्क|फॉर्म|फार्म)/i

export function extractActions(segments: Segment[]): Fact[] {
  return segments
    .filter((seg) => seg.text.length >= 15 && seg.text.length <= 320)
    .filter((seg) => !NOT_ACTION.test(seg.text))
    .filter(
      (seg) =>
        (ACTION_VERB.test(seg.text) && (DIRECTIVE.test(seg.text) || IMPERATIVE_START.test(seg.text))) ||
        (new RegExp(STRONG_DEADLINE.source, 'i').test(seg.text) &&
          DEADLINE_TASK.test(seg.text) &&
          DATE_PATTERNS.some(({ re }) => new RegExp(re.source, re.flags).test(seg.text)))
    )
    .slice(0, 4)
    .map((seg) => fact(seg.text, seg))
}

const WARNING = /(penalty|\bfine\b|late (payment )?(fee|surcharge)|disconnect|legal action|action (will|shall|may) be taken|prosecution|cancel|reject(ed)? if|failing which|otherwise|will not be (considered|accepted|processed)|not be entertained|liable|blacklist|seize|seizure|attachment of|arrest|warrant|ex[- ]?parte|forfeit|suspend|frozen|freeze|blocked|inoperative|जुर्माना|अर्थदंड|दंड|विलंब शुल्क|कार्रवाई|कार्यवाही की जाएगी|काट दिया|विच्छेद|निरस्त कर|रद्द कर|अन्यथा|नहीं तो|मान्य नहीं|स्वीकार नहीं|दायित्व|जब्त|कुर्की|गिरफ्तार|वारंट|एकपक्षीय|बंद कर दिया|दंडात्मक|কার্যব্যবস্থা|জরিমানা|বাতিল)/i

export function extractWarnings(segments: Segment[]): Fact[] {
  return segments
    .filter((seg) => seg.text.length >= 15 && seg.text.length <= 320 && WARNING.test(seg.text))
    .slice(0, 3)
    .map((seg) => fact(seg.text, seg))
}

// ── Documents the reader must bring/attach ────────────────────────────────────

const REQUIRED_DOCS: Array<{ id: RequiredDocId; re: RegExp }> = [
  // Hindi आधार also means "basis" ("तदर्थ आधार पर"), so require card/number context
  { id: 'aadhaar', re: /aadhaa?r|आधार\s*(?:कार्ड|संख्या|नंबर|नम्बर|नं|की प्रति|पत्र|card|no)|আধাৰ|আধার|ஆதார்|ఆధార్|ಆಧಾರ್|ആധാർ|આધાર|ਆਧਾਰ|ଆଧାର/i },
  { id: 'pan', re: /\bpan\b|पैन/i },
  { id: 'ration_card', re: /ration\s*card|राशन\s*कार्ड|शिधापत्रिका|রেশন কার্ড/i },
  { id: 'voter_id', re: /voter|\bepic\b|मतदाता पहचान|वोटर/i },
  { id: 'passbook', re: /pass\s*book|bank (account )?details|bank statement|cancelled cheque|पासबुक|बैंक खाता|बैंक विवरण/i },
  { id: 'photo', re: /photograph|\bphotos?\b|फोटो|फ़ोटो|छायाचित्र|ছবি/i },
  { id: 'income_certificate', re: /income certificate|आय प्रमाण|उत्पन्नाचा दाखला/i },
  { id: 'caste_certificate', re: /caste certificate|जाति प्रमाण|जातीचा दाखला/i },
  { id: 'domicile_certificate', re: /domicile|residence certificate|निवास प्रमाण|मूल निवास|अधिवास/i },
  { id: 'birth_certificate', re: /birth certificate|जन्म प्रमाण|जन्म दाखला/i },
  { id: 'land_record', re: /khatauni|khasra|7\/12|land record|भू[- ]?अभिलेख|खतौनी|खसरा|जमाबंदी|सातबारा/i },
  { id: 'electricity_bill', re: /electricity bill|बिजली (का )?बिल|वीज बिल/i },
  { id: 'address_proof', re: /address proof|proof of address|पते का प्रमाण|पता प्रमाण/i },
  { id: 'id_proof', re: /id(entity)? proof|proof of identity|photo id|पहचान पत्र|पहचान का प्रमाण|ओळखपत्र/i },
  { id: 'affidavit', re: /affidavit|शपथ\s*-?पत्र|हलफनामा|प्रतिज्ञापत्र/i },
  { id: 'application_form', re: /application form|आवेदन (फॉर्म|फार्म|प्रपत्र)|निर्धारित प्रपत्र|अर्जाचा नमुना/i }
]
const REQUIRED_CONTEXT = /(required|require|attach|enclose|bring|submit|along with|carry|produce|upload|documents?|copy of|copies|self[- ]attested|आवश्यक|जरूरी|ज़रूरी|संलग्न|साथ|लेकर|प्रस्तुत|जमा|दस्तावेज|कागजात|कागज़ात|प्रति|अपलोड|कागदपत्रे|आवश्यक|আবশ্যক|নথি|জমা|কপি|প্রতিলিপি|ப்படி|நகல்|சமர்ப்பி|ஆவணங்கள்|కాపీ|సమర్పించ|పత్రాలు|ಪ್ರತಿ|ಸಲ್ಲಿಸ|ದಾಖಲೆ|പകർപ്പ്|സമർപ്പി|രേഖകൾ|નકલ|જમા|રજૂ|દસ્તાવેજ|ਕਾਪੀ|ਨਕਲ|ਜਮ੍ਹਾ|ਦਸਤਾਵੇਜ਼|ନକଲ|ଜମା|ଦଲିଲ)/i
const REQUIRED_HEADING = /(documents? (required|needed|to be (submitted|attached|enclosed|brought))|enclosures?|following documents|attach the following|आवश्यक (दस्तावेज|कागजात|कागज़ात|प्रपत्र)|संलग्नक|निम्नलिखित (दस्तावेज|कागजात)|आवश्यक कागदपत्रे|প্রয়োজনীয় নথি)/i
const PAST_REFERENCE = /(submitted by|furnished by|on the basis of|based on|के आधार पर|द्वारा (प्रस्तुत|दिए|दिये))/i

/**
 * Indexes of segments that ask for documents ("bring your Aadhaar and income
 * certificate", or items under a "Documents required:" heading). These must not
 * count as evidence of the document's own type.
 */
export function documentRequestIndexes(segments: Segment[]): Set<number> {
  const indexes = new Set<number>()
  // A page naming 3+ different certificates/cards is a list of requirements, not one of them
  const distinctDocs = REQUIRED_DOCS.filter(({ re }) => segments.some((seg) => re.test(seg.text))).length
  const isList = distinctDocs >= 3
  let listUntil = -1
  segments.forEach((seg, index) => {
    if (REQUIRED_HEADING.test(seg.text)) listUntil = index + 10
    const mentionsDoc = REQUIRED_DOCS.some(({ re }) => re.test(seg.text))
    if (!mentionsDoc) return
    if (isList || index <= listUntil || (REQUIRED_CONTEXT.test(seg.text) && !PAST_REFERENCE.test(seg.text))) indexes.add(index)
  })
  return indexes
}

export function extractRequiredDocuments(segments: Segment[]): Fact<RequiredDocId>[] {
  const found = new Map<RequiredDocId, Fact<RequiredDocId>>()
  let listUntil = -1
  segments.forEach((seg, index) => {
    if (REQUIRED_HEADING.test(seg.text)) listUntil = index + 10
    const inList = index <= listUntil
    if (!inList && (!REQUIRED_CONTEXT.test(seg.text) || PAST_REFERENCE.test(seg.text))) return
    for (const { id, re } of REQUIRED_DOCS) {
      if (!found.has(id) && re.test(seg.text)) found.set(id, fact(id, seg))
    }
  })
  return [...found.values()]
}
